<template>
    <div v-if="stripePublicKey && !stripeError">
        <div v-show="elementReady && (!paymentStatus || paymentStatus === 'already_paid')">
            <div class="mb-3">
                {{
                    newCard
                        ? 'Enter your billing details.'
                        : 'Enter your new billing details. This will replace your current payment method.'
                }}
            </div>

            <validation-observer v-slot="{ handleSubmit, valid }">
                <form class="payment-method-form" @submit.stop.prevent="handleSubmit(onFormSubmit)">
                    <div class="row">
                        <div class="col-12">
                            <!-- Cardholder Name -->
                            <validation-provider v-slot="{ valid, errors }" rules="required" name="Cardholder name">
                                <b-form-group
                                    label="Cardholder name"
                                    :invalid-feedback="errors[0]"
                                    :state="errors[0] ? false : valid ? true : null"
                                >
                                    <b-form-input
                                        v-model="name"
                                        :state="errors[0] ? false : valid ? true : null"
                                        :disabled="savingCard || processing"
                                    />
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- Stripe card input -->
                        <div class="col-12 mb-3">
                            <label>Card</label>
                            <div ref="stripeCardContainerRef" class="stripe-card" />
                            <div class="stripe-error">
                                {{ stripeErrorMessage }}
                            </div>
                        </div>

                        <!-- Address 1 -->
                        <div class="col-6">
                            <validation-provider v-slot="{ valid, errors }" rules="required" name="Address line 1">
                                <b-form-group
                                    label="Address line 1"
                                    :invalid-feedback="errors[0]"
                                    :state="errors[0] ? false : valid ? true : null"
                                >
                                    <b-form-input
                                        v-model="addressLine1"
                                        :state="errors[0] ? false : valid ? true : null"
                                        :disabled="savingCard || processing"
                                    />
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- Address 2 -->
                        <div class="col-6">
                            <b-form-group label="Address line 2">
                                <b-form-input v-model="addressLine2" :disabled="savingCard || processing" />
                            </b-form-group>
                        </div>

                        <!-- City -->
                        <div class="col-6">
                            <validation-provider v-slot="{ valid, errors }" rules="required" name="City">
                                <b-form-group
                                    label="City"
                                    :invalid-feedback="errors[0]"
                                    :state="errors[0] ? false : valid ? true : null"
                                >
                                    <b-form-input
                                        v-model="city"
                                        :state="errors[0] ? false : valid ? true : null"
                                        :disabled="savingCard || processing"
                                    />
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- Country -->
                        <div class="col-6">
                            <div class="select-wrapper">
                                <b-form-group label="Country">
                                    <v-select
                                        v-model="country"
                                        :options="countriesList"
                                        :disabled="savingCard || processing"
                                        :clearable="false"
                                    >
                                        <template #open-indicator="{ attributes }">
                                            <!-- Remove default arrow -->
                                            <div v-bind="attributes" />
                                        </template>
                                    </v-select>
                                </b-form-group>
                            </div>
                        </div>

                        <!-- State -->
                        <div class="col-6">
                            <validation-provider v-slot="{ valid, errors }" rules="required" name="State">
                                <b-form-group
                                    label="State"
                                    :invalid-feedback="errors[0]"
                                    :state="errors[0] ? false : valid ? true : null"
                                >
                                    <b-form-input
                                        v-model="state"
                                        :state="errors[0] ? false : valid ? true : null"
                                        :disabled="savingCard || processing"
                                    />
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <!-- Postal Code -->
                        <div class="col-6">
                            <validation-provider v-slot="{ valid, errors }" rules="required" name="Postal Code">
                                <b-form-group
                                    label="Postal Code"
                                    :invalid-feedback="errors[0]"
                                    :state="errors[0] ? false : valid ? true : null"
                                >
                                    <b-form-input
                                        v-model="zip"
                                        :state="errors[0] ? false : valid ? true : null"
                                        :disabled="savingCard || processing"
                                    />
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <div v-if="errorMessage" class="col-12">
                            <div class="mb-3 error-message">
                                {{ errorMessage }}
                            </div>
                        </div>
                    </div>

                    <loader-btn
                        type="submit"
                        class="mt-3 float-right"
                        variant="primary"
                        :loading="processing || savingCard"
                        :disabled="!valid"
                    >
                        Update card
                    </loader-btn>
                </form>
            </validation-observer>
        </div>
        <div v-if="paymentStatus !== 'already_paid'">
            <div v-if="paymentStatus === 'paid'" class="mb-3">
                <div>Subscription payment succeeded!</div>

                <button class="btn btn-primary mt-3 float-right" @click="emitCardUpdatedEvent">Continue</button>
            </div>
            <div v-if="paymentStatus === 'payment_failed'" class="mb-3">
                <div>Subscription payment failed, please enter a different card.</div>

                <button class="btn btn-primary mt-3 float-right" @click="clearForm">Try again</button>
            </div>
        </div>
        <div v-show="!elementReady" class="d-flex justify-content-center">
            <b-spinner v-show="!elementReady" variant="primary" class="my-2" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, onMounted, ref, watch } from 'vue';
import { updateCard, updateCreditCardForShopifyUser } from '@/api/billing';
import { CardUpdateStatus } from '@/api/typings/billing';
import { Stripe, StripeCardElement, Token, StripeConstructorOptions, StripeElements } from '@stripe/stripe-js';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import vSelect from 'vue-select';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';

const props = defineProps<{
    store: string;
    stripePublicKey?: string;
    noSave?: boolean;
    fromPodBilling?: boolean;
    newCard?: boolean;
}>();

const emit = defineEmits(['card-updated', 'token']);

const stripeInstance = ref<Stripe | null>();
const stripeCardElements = ref<StripeCardElement | null>();
const stripeError = ref(false);
const stripeErrorMessage = ref('');

const elementReady = ref(false);
const processing = ref(false);
const savingCard = ref(false);
const errorMessage = ref('');

// Card details
const name = ref('');
const addressLine1 = ref('');
const addressLine2 = ref('');
const city = ref('');
const state = ref('');
const zip = ref('');
const country = ref({ label: 'United States', value: 'US' });

const STRIPE_ERRORS = {
    STRIPE_NOT_LOADED: 'Stripe v3 library is not loaded',
    INSTANCE_NOT_DEFINED: 'Instance object is not defined. Make sure you initialized Stripe before creating elements',
    ELEMENTS_NOT_DEFINED: "Elements object is not defined. You can't create stripe element without it",
    UNKNOWN_ERROR: 'An unknown error occurred.'
};

let lastToken = '';
const paymentStatus: Ref<CardUpdateStatus | null> = ref(null);

const countriesList: Record<string, string>[] = [
    { value: 'AF', label: 'Afghanistan' },
    { value: 'AL', label: 'Albania' },
    { value: 'DZ', label: 'Algeria' },
    { value: 'AS', label: 'American Samoa' },
    { value: 'AD', label: 'Andorra' },
    { value: 'AO', label: 'Angola' },
    { value: 'AI', label: 'Anguilla' },
    { value: 'AG', label: 'Antigua and Barbuda' },
    { value: 'AR', label: 'Argentina' },
    { value: 'AM', label: 'Armenia' },
    { value: 'AW', label: 'Aruba' },
    { value: 'AU', label: 'Australia' },
    { value: 'AT', label: 'Austria' },
    { value: 'AZ', label: 'Azerbaijan' },
    { value: 'BS', label: 'The Bahamas' },
    { value: 'BH', label: 'Bahrain' },
    { value: 'BD', label: 'Bangladesh' },
    { value: 'BB', label: 'Barbados' },
    { value: 'BY', label: 'Belarus' },
    { value: 'BE', label: 'Belgium' },
    { value: 'BZ', label: 'Belize' },
    { value: 'BJ', label: 'Benin' },
    { value: 'BM', label: 'Bermuda' },
    { value: 'BT', label: 'Bhutan' },
    { value: 'BO', label: 'Bolivia' },
    { value: 'BA', label: 'Bosnia and Herzegovina' },
    { value: 'BW', label: 'Botswana' },
    { value: 'BR', label: 'Brazil' },
    { value: 'VG', label: 'British Virgin Islands' },
    { value: 'BN', label: 'Brunei' },
    { value: 'BG', label: 'Bulgaria' },
    { value: 'BF', label: 'Burkina Faso' },
    { value: 'BI', label: 'Burundi' },
    { value: 'KH', label: 'Cambodia' },
    { value: 'CM', label: 'Cameroon' },
    { value: 'CA', label: 'Canada' },
    { value: 'CV', label: 'Cape Verde' },
    { value: 'KY', label: 'Cayman Islands' },
    { value: 'CF', label: 'Central African Republic' },
    { value: 'TD', label: 'Chad' },
    { value: 'CL', label: 'Chile' },
    { value: 'CN', label: 'China' },
    { value: 'CX', label: 'Christmas Island' },
    { value: 'CC', label: 'Cocos (Keeling) Islands' },
    { value: 'CO', label: 'Colombia' },
    { value: 'KM', label: 'Comoros' },
    { value: 'CG', label: 'Republic of the Congo' },
    { value: 'CK', label: 'Cook Islands' },
    { value: 'CR', label: 'Costa Rica' },
    { value: 'CI', label: "Cote d'Ivoire" },
    { value: 'HR', label: 'Croatia' },
    { value: 'CU', label: 'Cuba' },
    { value: 'CY', label: 'Cyprus' },
    { value: 'CZ', label: 'Czech Republic' },
    { value: 'DK', label: 'Denmark' },
    { value: 'DJ', label: 'Djibouti' },
    { value: 'DM', label: 'Dominica' },
    { value: 'DO', label: 'Dominican Republic' },
    { value: 'EC', label: 'Ecuador' },
    { value: 'EG', label: 'Egypt' },
    { value: 'SV', label: 'El Salvador' },
    { value: 'GQ', label: 'Equatorial Guinea' },
    { value: 'ER', label: 'Eritrea' },
    { value: 'EE', label: 'Estonia' },
    { value: 'ET', label: 'Ethiopia' },
    { value: 'FK', label: 'Falkland Islands (Islas Malvinas)' },
    { value: 'FO', label: 'Faroe Islands' },
    { value: 'FJ', label: 'Fiji' },
    { value: 'FI', label: 'Finland' },
    { value: 'FR', label: 'France' },
    { value: 'GF', label: 'French Guiana' },
    { value: 'PF', label: 'French Polynesia' },
    { value: 'GA', label: 'Gabon' },
    { value: 'GM', label: 'The Gambia' },
    { value: 'GE', label: 'Georgia' },
    { value: 'DE', label: 'Germany' },
    { value: 'GH', label: 'Ghana' },
    { value: 'GI', label: 'Gibraltar' },
    { value: 'GR', label: 'Greece' },
    { value: 'GL', label: 'Greenland' },
    { value: 'GD', label: 'Grenada' },
    { value: 'GP', label: 'Guadeloupe' },
    { value: 'GU', label: 'Guam' },
    { value: 'GT', label: 'Guatemala' },
    { value: 'GN', label: 'Guinea' },
    { value: 'GW', label: 'Guinea-Bissau' },
    { value: 'GY', label: 'Guyana' },
    { value: 'HT', label: 'Haiti' },
    { value: 'VA', label: 'Holy See (Vatican City)' },
    { value: 'HN', label: 'Honduras' },
    { value: 'HU', label: 'Hungary' },
    { value: 'IS', label: 'Iceland' },
    { value: 'IN', label: 'India' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'IR', label: 'Iran' },
    { value: 'IQ', label: 'Iraq' },
    { value: 'IE', label: 'Ireland' },
    { value: 'IL', label: 'Israel' },
    { value: 'IT', label: 'Italy' },
    { value: 'JM', label: 'Jamaica' },
    { value: 'JP', label: 'Japan' },
    { value: 'JO', label: 'Jordan' },
    { value: 'KZ', label: 'Kazakhstan' },
    { value: 'KE', label: 'Kenya' },
    { value: 'KI', label: 'Kiribati' },
    { value: 'KP', label: 'North Korea' },
    { value: 'KR', label: 'South Korea' },
    { value: 'KW', label: 'Kuwait' },
    { value: 'KG', label: 'Kyrgyzstan' },
    { value: 'LA', label: 'Laos' },
    { value: 'LV', label: 'Latvia' },
    { value: 'LB', label: 'Lebanon' },
    { value: 'LS', label: 'Lesotho' },
    { value: 'LR', label: 'Liberia' },
    { value: 'LY', label: 'Libya' },
    { value: 'LI', label: 'Liechtenstein' },
    { value: 'LT', label: 'Lithuania' },
    { value: 'LU', label: 'Luxembourg' },
    { value: 'MK', label: 'North Macedonia' },
    { value: 'MG', label: 'Madagascar' },
    { value: 'MW', label: 'Malawi' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'MV', label: 'Maldives' },
    { value: 'ML', label: 'Mali' },
    { value: 'MT', label: 'Malta' },
    { value: 'IM', label: 'Isle of Man' },
    { value: 'MH', label: 'Marshall Islands' },
    { value: 'MQ', label: 'Martinique' },
    { value: 'MR', label: 'Mauritania' },
    { value: 'MU', label: 'Mauritius' },
    { value: 'YT', label: 'Mayotte' },
    { value: 'MX', label: 'Mexico' },
    { value: 'FM', label: 'Federated States of Micronesia' },
    { value: 'MD', label: 'Moldova' },
    { value: 'MC', label: 'Monaco' },
    { value: 'MN', label: 'Mongolia' },
    { value: 'MS', label: 'Montserrat' },
    { value: 'MA', label: 'Morocco' },
    { value: 'MZ', label: 'Mozambique' },
    { value: 'MM', label: 'Myanmar (Burma)' },
    { value: 'NA', label: 'Namibia' },
    { value: 'NR', label: 'Nauru' },
    { value: 'NP', label: 'Nepal' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'AN', label: 'Netherlands Antilles' },
    { value: 'NC', label: 'New Caledonia' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'NI', label: 'Nicaragua' },
    { value: 'NE', label: 'Niger' },
    { value: 'NG', label: 'Nigeria' },
    { value: 'NU', label: 'Niue' },
    { value: 'NF', label: 'Norfolk Island' },
    { value: 'MP', label: 'Northern Mariana Islands' },
    { value: 'NO', label: 'Norway' },
    { value: 'OM', label: 'Oman' },
    { value: 'PK', label: 'Pakistan' },
    { value: 'PW', label: 'Palau' },
    { value: 'PS', label: 'Palestinian Territory' },
    { value: 'PA', label: 'Panama' },
    { value: 'PG', label: 'Papua New Guinea' },
    { value: 'PY', label: 'Paraguay' },
    { value: 'PE', label: 'Peru' },
    { value: 'PH', label: 'Philippines' },
    { value: 'PN', label: 'Pitcairn Islands' },
    { value: 'PL', label: 'Poland' },
    { value: 'PT', label: 'Portugal' },
    { value: 'PR', label: 'Puerto Rico' },
    { value: 'QA', label: 'Qatar' },
    { value: 'RE', label: 'Reunion' },
    { value: 'RO', label: 'Romania' },
    { value: 'RU', label: 'Russia' },
    { value: 'RW', label: 'Rwanda' },
    { value: 'KN', label: 'Saint Kitts and Nevis' },
    { value: 'LC', label: 'Saint Lucia' },
    { value: 'PM', label: 'Saint Pierre and Miquelon' },
    { value: 'VC', label: 'Saint Vincent and the Grenadines' },
    { value: 'SM', label: 'San Marino' },
    { value: 'ST', label: 'Sao Tome and Principe' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'SN', label: 'Senegal' },
    { value: 'SC', label: 'Seychelles' },
    { value: 'SL', label: 'Sierra Leone' },
    { value: 'SG', label: 'Singapore' },
    { value: 'SK', label: 'Slovakia' },
    { value: 'SI', label: 'Slovenia' },
    { value: 'SB', label: 'Solomon Islands' },
    { value: 'SO', label: 'Somalia' },
    { value: 'ZA', label: 'South Africa' },
    { value: 'ES', label: 'Spain' },
    { value: 'LK', label: 'Sri Lanka' },
    { value: 'SD', label: 'Sudan' },
    { value: 'SR', label: 'Suriname' },
    { value: 'SJ', label: 'Svalbard' },
    { value: 'SZ', label: 'Eswatini' },
    { value: 'SE', label: 'Sweden' },
    { value: 'CH', label: 'Switzerland' },
    { value: 'SY', label: 'Syria' },
    { value: 'TW', label: 'Taiwan' },
    { value: 'TJ', label: 'Tajikistan' },
    { value: 'TZ', label: 'Tanzania' },
    { value: 'TH', label: 'Thailand' },
    { value: 'TG', label: 'Togo' },
    { value: 'TK', label: 'Tokelau' },
    { value: 'TO', label: 'Tonga' },
    { value: 'TT', label: 'Trinidad and Tobago' },
    { value: 'TN', label: 'Tunisia' },
    { value: 'TR', label: 'Turkey' },
    { value: 'TM', label: 'Turkmenistan' },
    { value: 'TC', label: 'Turks and Caicos Islands' },
    { value: 'TV', label: 'Tuvalu' },
    { value: 'UG', label: 'Uganda' },
    { value: 'UA', label: 'Ukraine' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'US', label: 'United States' },
    { value: 'UM', label: 'United States Minor Outlying Islands' },
    { value: 'UY', label: 'Uruguay' },
    { value: 'UZ', label: 'Uzbekistan' },
    { value: 'VU', label: 'Vanuatu' },
    { value: 'VE', label: 'Venezuela' },
    { value: 'VN', label: 'Vietnam' },
    { value: 'VI', label: 'Virgin Islands' },
    { value: 'WF', label: 'Wallis and Futuna' },
    { value: 'EH', label: 'Western Sahara' },
    { value: 'WS', label: 'Western Samoa' },
    { value: 'YE', label: 'Yemen' },
    { value: 'CD', label: 'Democratic Republic of the Congo' },
    { value: 'ZM', label: 'Zambia' },
    { value: 'ZW', label: 'Zimbabwe' },
    { value: 'HK', label: 'Hong Kong' },
    { value: 'MO', label: 'Macau' },
    { value: 'AQ', label: 'Antarctica' },
    { value: 'BV', label: 'Bouvet Island' },
    { value: 'IO', label: 'British Indian Ocean Territory' },
    { value: 'TF', label: 'French Southern and Antarctic Lands' },
    { value: 'HM', label: 'Heard Island and McDonald Islands' },
    { value: 'SH', label: 'Saint Helena' },
    { value: 'GS', label: 'South Georgia and the South Sandwich Islands' },
    { value: 'GG', label: 'Guernsey' },
    { value: 'RS', label: 'Serbia' },
    { value: 'BL', label: 'Saint Barthélemy' },
    { value: 'ME', label: 'Montenegro' },
    { value: 'JE', label: 'Jersey' },
    { value: 'CW', label: 'Curaçao' },
    { value: 'MF', label: 'Saint Martin' },
    { value: 'SX', label: 'Sint Maarten' },
    { value: 'TL', label: 'Timor-Leste' },
    { value: 'SS', label: 'South Sudan' },
    { value: 'AX', label: 'Åland Islands' },
    { value: 'BQ', label: 'Bonaire' },
    { value: 'XK', label: 'Republic of Kosovo' }
];

const stripeCardContainerRef: Ref<HTMLDivElement | null> = ref(null);

watch([processing, savingCard], () => {
    if (stripeCardElements.value) {
        stripeCardElements.value.update({
            disabled: processing.value || savingCard.value
        });
    }
});

onMounted(() => {
    try {
        clearForm();
        if (!props.stripePublicKey) throw new Error(STRIPE_ERRORS.STRIPE_NOT_LOADED);

        stripeInstance.value = initStripe(props.stripePublicKey);
        if (!stripeInstance.value) throw new Error(STRIPE_ERRORS.INSTANCE_NOT_DEFINED);

        const stripeElements = createStripeElements(stripeInstance.value);
        if (!stripeElements) throw new Error(STRIPE_ERRORS.ELEMENTS_NOT_DEFINED);

        stripeCardElements.value = createStripeElement(stripeElements, {
            hidePostalCode: true
        });
        if (!stripeCardElements.value) throw new Error(STRIPE_ERRORS.ELEMENTS_NOT_DEFINED);

        stripeCardElements.value.mount(stripeCardContainerRef.value!);
        stripeCardElements.value.on('ready', () => {
            elementReady.value = true;
        });
    } catch (error) {
        if (error instanceof Error) {
            console.error(error);
            stripeErrorMessage.value = error.message || STRIPE_ERRORS.UNKNOWN_ERROR;
        } else {
            console.error(STRIPE_ERRORS.UNKNOWN_ERROR);
            stripeErrorMessage.value = STRIPE_ERRORS.UNKNOWN_ERROR;
        }
        stripeError.value = true;
    }
});

const emitCardUpdatedEvent = () => {
    emit('card-updated', {
        token: lastToken,
        result: paymentStatus.value
    });
};

const initStripe = (key: string, options?: StripeConstructorOptions): Stripe | null => {
    try {
        if (!window.Stripe) {
            throw new Error(STRIPE_ERRORS.STRIPE_NOT_LOADED);
        }
        const stripeInstance: Stripe = window.Stripe(key, options);
        return stripeInstance;
    } catch (error) {
        console.error(error);
        stripeError.value = true;
        return null;
    }
};

const createStripeElements = (instance: Stripe) => {
    try {
        const elements: StripeElements = instance.elements();
        return elements;
    } catch (error) {
        console.error(error);
        stripeError.value = true;
        return null;
    }
};

const createStripeElement = (
    elements: StripeElements,
    options?: {
        hidePostalCode?: boolean;
    }
) => {
    try {
        const element = elements.create('card', options);
        return element;
    } catch (error) {
        console.error(error);
        stripeError.value = true;
        return null;
    }
};

const tokenCreated = async (token: Token) => {
    if (!props.store) {
        console.warn(`No store given on CreditCardInput`);
        return;
    }

    if (!props.noSave) {
        savingCard.value = true;
        try {
            lastToken = token.id;

            // Update the user with the new card
            if (props.fromPodBilling) {
                await updateCreditCardForShopifyUser(token.id);
                emitCardUpdatedEvent();
            } else {
                const result = await updateCard(props.store, token.id);

                if (result.Status) {
                    paymentStatus.value = result.Status;

                    // If the subscription is already paid, emit the event
                    if (paymentStatus.value === 'already_paid') {
                        emitCardUpdatedEvent();
                    }
                } else {
                    emitCardUpdatedEvent();
                }
            }
        } catch (error) {
            window.rumReportError(error as Error);
            errorMessage.value = 'Error updating card.';
        } finally {
            savingCard.value = false;
        }
    } else {
        emit('token', token.id);
    }
};

const onFormSubmit = async () => {
    if (!stripeInstance.value || !stripeCardElements.value) return;
    try {
        processing.value = true;
        const { token, error } = await stripeInstance.value.createToken(stripeCardElements.value, {
            name: name.value,
            address_line1: addressLine1.value,
            address_line2: addressLine2.value,
            address_city: city.value,
            address_state: state.value,
            address_zip: zip.value,
            address_country: country.value.value
        });

        if (error) {
            stripeErrorMessage.value = error.message || '';
            return;
        }
        if (token) {
            tokenCreated(token);
        }
    } catch (error) {
        errorMessage.value = 'Error updating credit card.';
        window.rumReportError(error as Error);
    } finally {
        processing.value = false;
    }
};

const clearForm = () => {
    name.value = '';
    addressLine1.value = '';
    addressLine2.value = '';
    city.value = '';
    state.value = '';
    zip.value = '';
    country.value = { label: 'United States', value: 'US' };
    paymentStatus.value = null;
    lastToken = '';
    if (!stripeCardElements.value) return;
    stripeCardElements.value.clear();
};
</script>

<style scoped lang="scss">
.stripe-error {
    color: red;
}

.stripe-card {
    box-sizing: border-box;
    height: 40px;
    padding: 10px 12px;
    border: 1px solid transparent;
    border-radius: 4px;
    background-color: white;
    box-shadow: 0 1px 3px 0 #e6ebf1;
    transition: box-shadow 150ms ease;
}
</style>

<style lang="scss" scoped>
.v-select {
    // Same arrow style used for bootstrap dropdown
    background: #fff
        url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23343a40' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e")
        no-repeat right 0.75rem center/8px 10px;

    .vs__selected {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: inherit;
        max-width: 230px;
    }

    .vs__actions {
        padding-right: 12px !important;
    }

    .vs__dropdown-menu {
        width: 100%;
    }

    .vs__selected-options {
        padding-top: 2px;
    }

    .vs__dropdown-toggle {
        min-height: 38px;
        max-height: 38px;
    }
}
</style>
